import { Button, DialogContentText } from "@material-ui/core";
import ActionDialog from "@saleor/components/ActionDialog";
import { PAGINATE_BY } from "@saleor/config";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import { commonMessages } from "@saleor/intl";
import { joinDateTime, maybe } from "@saleor/misc";
import PageheaderDeleteDialg from "@saleor/pageheaders/components/PageheaderDeleteDialog";
import PageheaderPage, {
  PageheaderPageFormData
} from "@saleor/pageheaders/components/PageheaderPage";
import {
  usePageheaderBulkApplyMutation,
  usePageheaderDeleteMutation,
  usePageheaderUpdateMutation
} from "@saleor/pageheaders/mutations";
import { usePageheaderDetailsQuery } from "@saleor/pageheaders/queries";
import {
  pageheaderListUrl,
  pageheaderUrl,
  PageheaderUrlDialog,
  PageheaderUrlQueryParams
} from "@saleor/pageheaders/urls";
import { PageHeaderLocatedEnum } from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

interface PageheaderDetailsProps {
  id: string;
  params: PageheaderUrlQueryParams;
}

const PageheaderDetails: React.FC<PageheaderDetailsProps> = ({
  id,
  params
}) => {
  const navigate = useNavigator();
  const paginate = usePaginator();
  const notify = useNotifier();
  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );
  const intl = useIntl();
  const [openModal, closeModal] = createDialogActionHandlers<
    PageheaderUrlDialog,
    PageheaderUrlQueryParams
  >(navigate, params => pageheaderUrl(id, params), params);

  const paginationState = createPaginationState(PAGINATE_BY, params);

  const { data, loading } = usePageheaderDetailsQuery({
    variables: {
      id,
      ...paginationState
    }
  });

  const [pageheaderDelete, pageheaderDeleteOpts] = usePageheaderDeleteMutation({
    onCompleted: data => {
      if (data.pageHeaderDelete.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "pageheader delete"
          })
        });
        navigate(pageheaderListUrl());
      }
    }
  });

  const [pageheaderUpdate, pageheaderUpdateOpts] = usePageheaderUpdateMutation({
    onCompleted: data => {
      if (data.pageHeaderUpdate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
      }
    }
  });

  const handleUpdate = async (data: PageheaderPageFormData) => {
    const input = {
      name: data.name,
      isDefault: data.isDefault,
      isTop:
        data.located === PageHeaderLocatedEnum.CHECKOUT ? false : data.isTop,
      pcContent: data.pcContent,
      mobileContent: data.mobileContent,
      located: data.located,
      startDate: joinDateTime(data.startDate, data.startTime),
      endDate: joinDateTime(data.endDate, data.endTime),
      countdown: data.countdown,
      pcCountdown: data.pcCountdown,
      mbCountdown: data.mbCountdown,
      languageOpt: data.languageOpt,
      mainColor: data.mainColor,
      secondaryColor: data.secondaryColor,
      pcRollingDuration: data.pcRollingDuration,
      mbRollingDuration: data.mbRollingDuration
    };

    const result = await pageheaderUpdate({
      variables: {
        id,
        input
      }
    });

    return result.data.pageHeaderUpdate.errors;
  };

  const [applyPageheader, applyPageheaderOpts] = usePageheaderBulkApplyMutation(
    {
      onCompleted: data => {
        if (data.pageHeaderBulkApply.errors.length === 0) {
          notify({
            status: "success",
            text: intl.formatMessage({
              defaultMessage: "Applied custom page from pageheader"
            })
          });
          reset();
          closeModal();
        }
      }
    }
  );

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    data?.pageHeader.pageTypes?.pageInfo,
    paginationState,
    params
  );

  const canOpenBulkActionDialog = maybe(() => params.ids.length > 0);

  return (
    <>
      <PageheaderPage
        errors={pageheaderUpdateOpts.data?.pageHeaderUpdate.errors || []}
        pageheader={maybe(() => data.pageHeader)}
        disabled={loading}
        onBack={() => navigate(pageheaderListUrl())}
        saveButtonBarState={pageheaderUpdateOpts.status}
        onDelete={() => openModal("remove")}
        onSubmit={handleUpdate}
        title={"details"}
        pageInfo={pageInfo}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        pageTypeListToolbar={
          <Button
            color="primary"
            onClick={() =>
              openModal("apply-pageheader", {
                ids: listElements
              })
            }
          >
            <FormattedMessage
              defaultMessage="Apply Pageheader"
              description="Apply the pageheader to the custom page by url"
              id="pageheaderDetailsApplyUrl"
            />
          </Button>
        }
        isChecked={isSelected}
        selected={listElements.length}
        toggle={toggle}
        toggleAll={toggleAll}
      ></PageheaderPage>
      <PageheaderDeleteDialg
        open={params.action === "remove"}
        title={maybe(() => data.pageHeader.name, "……")}
        confirmButtonState={pageheaderDeleteOpts.status}
        onClose={closeModal}
        onConfirm={() =>
          pageheaderDelete({
            variables: {
              id
            }
          })
        }
      />
      <ActionDialog
        open={params.action === "apply-pageheader"}
        title={intl.formatMessage({
          defaultMessage: "Apply Pageheader to Custom Page",
          description: "dialog header"
        })}
        confirmButtonState={applyPageheaderOpts.status}
        onClose={closeModal}
        onConfirm={() =>
          applyPageheader({
            variables: {
              ...paginationState,
              pageHeaderId: data.pageHeader.id,
              ids: params.ids
            }
          })
        }
      >
        {canOpenBulkActionDialog && (
          <DialogContentText>
            <FormattedMessage
              defaultMessage="{counter,plural,one{Are you sure you want to apply this custom page?} other{Are you sure you want to apply {displayQuantity} custom pages?}}"
              description="dialog content"
              values={{
                counter: params.ids.length,
                displayQuantity: <strong>{params.ids.length}</strong>
              }}
            />
          </DialogContentText>
        )}
      </ActionDialog>
    </>
  );
};

export default PageheaderDetails;
